"use server";

import {
  addRacArisRuleSchema,
  editRacArisRuleSchema,
  deleteRacArisRuleSchema,
} from "@/lib/schemas/rac-aris-rules";

import { validateParams } from "@/lib/utils";

import { prisma } from "@/prisma";

import z from "zod";

import { requireServerRole } from "@/lib/guards";

import { GRP_ROLE_ADMIN } from "@/lib/grp";

import { ActionResult } from "@/types/server-action";

import { v4 as uuidv4 } from "uuid";

export async function addRacArisRule(
  params: z.infer<typeof addRacArisRuleSchema>,
): Promise<ActionResult> {
  await requireServerRole([GRP_ROLE_ADMIN]);

  try {
    const {
      kvps,
      renterIdentifier,
      financialOrganizationIdentifier,
      renterOperatorIdentifier,
      isActive,
    } = validateParams(addRacArisRuleSchema, params);

    const existingRule = await prisma.immatriculationRacArisRule.findFirst({
      where: {
        kvps,
        renterIdentifier,
        financialOrganizationIdentifier,
        renterOperatorIdentifier,
        deletedAt: null,
      },
    });

    if (existingRule) {
      return {
        success: false,
        message: "Une règle active existe déjà.",
      };
    }

    const item = await prisma.immatriculationRacArisRule.create({
      data: {
        kvps,
        renterIdentifier,
        financialOrganizationIdentifier,
        renterOperatorIdentifier,
        isActive,
        versionGroupId: uuidv4(),
        versionNumber: 1,
      },
    });

    return {
      success: true,
      data: item,
    };
  } catch (error) {
    console.error(error);

    return {
      success: false,
      message: "Une erreur serveur est survenue.",
    };
  }
}

export async function editRacArisRule(
  params: z.infer<typeof editRacArisRuleSchema>,
): Promise<ActionResult> {
  await requireServerRole([GRP_ROLE_ADMIN]);

  try {
    const {
      id,
      kvps,
      renterIdentifier,
      financialOrganizationIdentifier,
      renterOperatorIdentifier,
      isActive = true,
    } = validateParams(editRacArisRuleSchema, params);

    const current = await prisma.immatriculationRacArisRule.findUnique({
      where: { id },
    });

    if (!current) {
      return {
        success: false,
        message: "Règle introuvable.",
      };
    }

    const conflictRule = await prisma.immatriculationRacArisRule.findFirst({
      where: {
        kvps,
        renterIdentifier,
        financialOrganizationIdentifier,
        renterOperatorIdentifier,
        deletedAt: null,
        NOT: {
          id,
        },
      },
    });

    if (conflictRule) {
      return {
        success: false,
        message: "Une règle active existe déjà avec cette configuration.",
      };
    }

    const newVersion = await prisma.$transaction(async (tx) => {
      await tx.immatriculationRacArisRule.update({
        where: {
          id,
        },
        data: {
          isActive: false,
          deletedAt: new Date(),
        },
      });

      return tx.immatriculationRacArisRule.create({
        data: {
          kvps,
          renterIdentifier,
          financialOrganizationIdentifier,
          renterOperatorIdentifier,
          isActive,
          versionGroupId: current.versionGroupId,
          versionNumber: current.versionNumber + 1,
        },
      });
    });

    return {
      success: true,
      data: newVersion,
    };
  } catch (error) {
    console.error(error);

    return {
      success: false,
      message: "Une erreur serveur est survenue.",
    };
  }
}

export async function deleteRacArisRule(
  params: z.infer<typeof deleteRacArisRuleSchema>,
): Promise<ActionResult> {
  await requireServerRole([GRP_ROLE_ADMIN]);

  try {
    const { id } = validateParams(deleteRacArisRuleSchema, params);

    const record = await prisma.immatriculationRacArisRule.findUnique({
      where: { id },
    });

    if (!record) {
      return {
        success: false,
        message: "Règle introuvable.",
      };
    }

    await prisma.immatriculationRacArisRule.updateMany({
      where: {
        versionGroupId: record.versionGroupId,
      },
      data: {
        deletedAt: new Date(),
        isActive: false,
      },
    });

    return {
      success: true,
    };
  } catch (error) {
    console.error(error);

    return {
      success: false,
      message: "Une erreur serveur est survenue.",
    };
  }
}
